import { CONFIG_KEY } from '~/store/States/global';

export class Encryption {
  constructor() {
    this.secretKeyHex = CONFIG_KEY;
    this.cryptoKey = null;
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized) return true;

    if (!this.isSupported()) {
      throw new Error('Web Crypto API no está soportada');
    }

    try {
      const keyBuffer = this.hexToArrayBuffer(this.secretKeyHex);

      this.cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );

      this.isInitialized = true;
      return true;
    } catch (error) {
      throw new Error('Falló la inicialización: ' + error.message);
    }
  }

  isSupported() {
    return (
      typeof window !== 'undefined' &&
      window.crypto &&
      window.crypto.subtle &&
      typeof window.crypto.subtle.encrypt === 'function'
    );
  }

  hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes.buffer;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  base64ToArrayBuffer(base64) {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async encrypt(data) {
    if (!this.isInitialized) {
      await this.init();
    }

    const dataString = typeof data === 'object' ? JSON.stringify(data) : String(data);
    const dataBuffer = new TextEncoder().encode(dataString);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv, tagLength: 128 },
      this.cryptoKey,
      dataBuffer
    );

    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);

    return this.arrayBufferToBase64(combined.buffer);
  }

  async decrypt(encryptedData) {
    if (!this.isInitialized) {
      await this.init();
    }

    const combined = this.base64ToArrayBuffer(encryptedData);
    const combinedBytes = new Uint8Array(combined);

    if (combinedBytes.length < 12) {
      throw new Error('Datos demasiado cortos');
    }

    const iv = combinedBytes.slice(0, 12);
    const encryptedWithTag = combinedBytes.slice(12);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv, tagLength: 128 },
      this.cryptoKey,
      encryptedWithTag
    );

    const decryptedString = new TextDecoder().decode(decryptedBuffer);

    try {
      return JSON.parse(decryptedString);
    } catch {
      return decryptedString;
    }
  }

}
